import { ChangeDetectorRef, Directive, Input, OnDestroy } from '@angular/core';
import { ReportDefinitions } from 'generated/graphql-types';
import { ReplaySubject } from 'rxjs';
import { formatPhoneNumbers } from 'src/app/pipes/format-phone-numbers/format-phone-numbers.pipe';
import { ReportWrapperComponentBase } from './report-wrapper-component-base';
import { ReportBarGraphSource } from './shared/models/bar-graph-source-report-parameter';
import { ReportOutputBase } from './shared/models/report-output-base';
import { ReportOutputContainer } from './shared/report-output-container/report-output-container';

export interface ReportOutputComponent<TReport> {
   /**
    *
    */
   handleReportChange?(): void;
}

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class ReportOutputComponent<TReport extends ReportOutputBase<any, any> = ReportOutputBase<any, any>>
   extends ReportWrapperComponentBase
   implements OnDestroy
{
   barPadding = 8;

   @Input()
   set report(report: TReport | null) {
      this.report$.next(report);
      this._report = report;

      if (this.handleReportChange) {
         this.handleReportChange();
      }

      this.setTabbedReportParameterTypeOutput(report);
      switch (report?.report) {
         case ReportDefinitions.DailyCallVolume:
         case ReportDefinitions.WeekDayCallVolume:
            this.setGraphDataDayReport(report);
            this.setBarPadding(this._report?.data?.days?.data?.length);
            break;
         case ReportDefinitions.HourlyCallVolume:
            this.setGraphDataHourReport(report);
            this.setBarPadding(this._report?.data?.days?.data?.length);
            break;
         case ReportDefinitions.InboundCallsByPhoneNumber:
            this.setGraphDataPhoneNumberReport(report);
            this.setBarPadding(this._report?.data?.phoneNumbers?.data?.length);
            break;
      }
   }

   get report(): TReport | null {
      return this._report;
   }

   outputContainer$ = new ReplaySubject<ReportOutputContainer<any>>(1);

   report$ = new ReplaySubject<TReport | null>(1);

   phoneNumbersFromReportParameter: string[] = [];

   graphDataFromHoursReportParamter: any = [];

   graphDataFromDayReportParameter: any = [];

   graphDataFromPhoneNumberReportParameter: any = [];

   protected _report: TReport | null = null;

   constructor(protected changeDetectorRef: ChangeDetectorRef) {
      super();
   }

   ngOnDestroy(): void {
      this.outputContainer$.complete();
   }

   registerOutputContainer(outputContainer: ReportOutputContainer<any>) {
      this.outputContainer$.next(outputContainer);
   }

   setReportData(report: TReport | null): void {
      this.report = report;
      this.changeDetectorRef.markForCheck();
   }

   private setTabbedReportParameterTypeOutput(report: TReport | null): void {
      this.phoneNumbersFromReportParameter = report?.parameters.phoneNumbers?.names?.map((name) => {
         return formatPhoneNumbers(name);
      });
   }
   private setGraphDataHourReport(report: TReport | null): void {
      if (report && report.data && report.data.hours && report.data.hours.data) {
         this.graphDataFromHoursReportParamter =
            report.data.hours.data.map((hoursReportData) => {
               return {
                  name: hoursReportData.hour,
                  value: hoursReportData.count != null ? hoursReportData.count : 0,
               };
            }) || [];
      }
   }

   private setGraphDataPhoneNumberReport(report: TReport | null): void {
      if (report && report.data && report.data.phoneNumbers && report.data.phoneNumbers.data) {
         this.graphDataFromPhoneNumberReportParameter = report.data.phoneNumbers.data.map((phoneNumberReportData) => {
            return {
               name: phoneNumberReportData.name,
               value: phoneNumberReportData.count != null ? phoneNumberReportData.count : 0,
            };
         });
      }
   }

   private setGraphDataDayReport(report: TReport | null): void {
      const secondsInHour = 3600;
      if (report?.data?.days?.data) {
         this.graphDataFromDayReportParameter = report.data.days.data.map((daysReportData) => {
            const isCountSource = report.parameters.barGraphSource.value == ReportBarGraphSource.Count;
            const count = daysReportData.count != null ? daysReportData.count : 0;
            const duration = daysReportData.duration != null ? daysReportData.duration / secondsInHour : 0;

            return {
               name:
                  report.report === ReportDefinitions.WeekDayCallVolume
                     ? daysReportData.weekDay
                     : daysReportData.day.split('T')[0],

               value: isCountSource ? count : duration,
            };
         });
      }
   }

   protected setBarPadding(dataLength: number) {
      this.barPadding = 8;
      if (dataLength < 32) {
         this.barPadding = 8;
      } else if (dataLength < 60) {
         this.barPadding = 4;
      } else {
         this.barPadding = 1;
      }
   }
}
